import React, { useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaExternalLinkAlt, FaInfoCircle } from "react-icons/fa";
import { FaCopy } from "react-icons/fa6";
import { usePostMultiplacerDevicesSimilarRoomsWithDevicesQuery } from "../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getPercentColor } from "../../../RoomColoring";
import { CurrentRoomComparisonViewer } from "./CurrentRoomComparisonViewer";
import {
  selectCeilingShowOnlyLockedRooms,
  setCeilingSelectedSimilarRoomWithDevicesNumber,
  setCeilingShowOnlyLockedRooms,
} from "../ceilingSlice";
import { SimilarRoomWithDevicesViewer } from "./SimilarRoomWithDevicesViewer";
import {
  selectCachedSimilarRoomsWithDevices,
  selectSelectedSimilarRoomWithDevices,
  selectSimilarRoomWithDevicesCards,
  selectSimilarRoomsWithDevicesCurrentRoomName,
  selectSimilarRoomsWithDevicesCurrentRoomNumber,
  selectSimilarRoomsWithDevicesQueryArgs,
  selectSimilarRoomsWithDevicesSortedBySimilarity,
} from "./similarRoomsWithDevicesSelectors";
import { selectCopySimilarRoomWithDevicesToCurrentRoomState } from "./similarRoomWithDevicesViewerSelectors";
import { thunkCopySimilarRoomWithDevicesToCurrentRoom } from "./thunkCopySimilarRoomWithDevicesToCurrentRoom";

const getSimilarityPercent = (score: number): number => Math.round(score * 100);
const formatSimilarity = (score: number): string => `${getSimilarityPercent(score)} %`;
const compactBestMatches = (rooms: { roomNumber: string; similarityScore: number }[]): string =>
  rooms
    .slice(0, 3)
    .map((match) => `${match.roomNumber} (${formatSimilarity(match.similarityScore)})`)
    .join(", ");

type SimilarRoomsWithDevicesModalProps = {
  onClose: () => void;
};

const SimilarRoomsWithDevicesModal: React.FC<SimilarRoomsWithDevicesModalProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const cards = useAppSelector(selectSimilarRoomWithDevicesCards);
  const roomName = useAppSelector(selectSimilarRoomsWithDevicesCurrentRoomName);
  const selectedRoom = useAppSelector(selectSelectedSimilarRoomWithDevices);
  const copyState = useAppSelector(selectCopySimilarRoomWithDevicesToCurrentRoomState);

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Lignende prosjekterte ${roomName ?? ""}-rom`}
      size="90vw"
      centered
    >
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.45fr) minmax(320px, 0.9fr)",
          gap: 16,
          height: "70vh",
          minHeight: 420,
        }}
      >
        <Paper withBorder radius="sm" p="md" style={{ minHeight: 0 }}>
          <Stack gap="sm" h="100%">
            <Group justify="space-between" align="center" gap="xs">
              <Text size="sm" fw={600}>
                Rom med eksisterende prosjektering
              </Text>
              <Group gap="xs" wrap="nowrap">
                {selectedRoom && (
                  <Badge
                    variant="filled"
                    styles={{
                      root: {
                        backgroundColor: getPercentColor(
                          getSimilarityPercent(selectedRoom.similarityScore)
                        ),
                        color: "#ffffff",
                      },
                    }}
                  >
                    {formatSimilarity(selectedRoom.similarityScore)}
                  </Badge>
                )}
                {copyState.showCopyButton && (
                  <Button
                    size="xs"
                    variant="filled"
                    color="blue"
                    disabled={copyState.disabled}
                    leftSection={<FaCopy size={12} />}
                    onClick={() => dispatch(thunkCopySimilarRoomWithDevicesToCurrentRoom())}
                  >
                    Kopier til aktuelt rom
                  </Button>
                )}
              </Group>
            </Group>
            <Text size="xs" c={copyState.disabled ? "dimmed" : "blue.7"}>
              {copyState.hint}
            </Text>

            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(210px, 280px) minmax(0, 1fr)",
                gap: 12,
                flex: 1,
                minHeight: 0,
              }}
            >
              <ScrollArea style={{ minHeight: 0 }}>
                <Stack gap="xs" pr="xs">
                  {cards.map((card) => {
                    return (
                      <Paper
                        key={card.roomNumber}
                        withBorder
                        radius="sm"
                        p="sm"
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          dispatch(setCeilingSelectedSimilarRoomWithDevicesNumber(card.roomNumber))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            dispatch(
                              setCeilingSelectedSimilarRoomWithDevicesNumber(card.roomNumber)
                            );
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          borderColor: card.isSelected
                            ? card.similarityColor
                            : "var(--mantine-color-gray-3)",
                          backgroundColor: card.isSelected ? `${card.similarityColor}14` : "#ffffff",
                        }}
                      >
                        <Stack gap={6}>
                          <Group justify="space-between" gap="xs" wrap="nowrap">
                            <Box style={{ minWidth: 0 }}>
                              <Text size="sm" fw={600} truncate="end">
                                {card.roomNumber}
                              </Text>
                              <Text size="xs" c="dimmed" truncate="end">
                                {card.roomName}
                              </Text>
                            </Box>
                            <Badge
                              variant="filled"
                              styles={{
                                root: {
                                  backgroundColor: card.similarityColor,
                                  color: "#ffffff",
                                  flexShrink: 0,
                                },
                              }}
                            >
                              {card.similarityPercent} %
                            </Badge>
                          </Group>
                          <Group gap={6} wrap="wrap">
                            <Badge variant="light" color="gray" size="xs">
                              Etasje: {card.floorLabel}
                            </Badge>
                            <Badge variant="light" color={card.locked ? "green" : "gray"} size="xs">
                              {card.locked ? "Låst" : "Ikke låst"}
                            </Badge>
                          </Group>
                          {card.locked && (
                            <Stack gap={1}>
                              {card.lockedByLabel && (
                                <Text size="xs" c="dimmed" truncate="end">
                                  Låst av {card.lockedByLabel}
                                </Text>
                              )}
                              {card.lockedAtLabel && (
                                <Text size="xs" c="dimmed">
                                  Låst {card.lockedAtLabel}
                                </Text>
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </ScrollArea>

              <Box style={{ minHeight: 0 }}>
                <SimilarRoomWithDevicesViewer />
              </Box>
            </Box>
          </Stack>
        </Paper>

        <Paper withBorder radius="sm" p="md" style={{ minHeight: 0 }}>
          <Stack h="100%" gap="xs">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                Aktuellt rum
              </Text>
              <Text size="xs" c="dimmed">
                {roomName}
              </Text>
            </Stack>
            <Box style={{ flex: 1, minHeight: 0, width: "100%" }}>
              <CurrentRoomComparisonViewer />
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Modal>
  );
};

export const SimilarRoomsWithDevicesBanner: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const showOnlyLockedRooms = useAppSelector(selectCeilingShowOnlyLockedRooms);
  const roomNumber = useAppSelector(selectSimilarRoomsWithDevicesCurrentRoomNumber);
  const roomName = useAppSelector(selectSimilarRoomsWithDevicesCurrentRoomName);
  const args = useAppSelector(selectSimilarRoomsWithDevicesQueryArgs);

  const { isLoading, isError } = usePostMultiplacerDevicesSimilarRoomsWithDevicesQuery(
    args ?? skipToken
  );

  const matchingRooms = useAppSelector(selectCachedSimilarRoomsWithDevices);
  const sortedMatchingRooms = useAppSelector(selectSimilarRoomsWithDevicesSortedBySimilarity);

  if (!args || !roomNumber || !roomName) {
    return null;
  }

  if (isLoading) {
    return (
      <Alert variant="light" color="blue" icon={<FaInfoCircle size={14} />} py="xs">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="xs" truncate="end">
            Sjekker lignende rom med Multiplacer-komponenter...
          </Text>
          <Checkbox
            size="xs"
            label="Kun låste"
            checked={showOnlyLockedRooms}
            onChange={(event) =>
              dispatch(setCeilingShowOnlyLockedRooms(event.currentTarget.checked))
            }
          />
        </Group>
      </Alert>
    );
  }

  if (isError) {
    return null;
  }

  if (matchingRooms.length === 0) {
    return (
      <Alert variant="light" color="gray" icon={<FaInfoCircle size={14} />} py="xs">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="xs" truncate="end">
            Ingen lignende rom med Multiplacer-komponenter kunne hittes.
          </Text>
          <Checkbox
            size="xs"
            label="Kun låste"
            checked={showOnlyLockedRooms}
            onChange={(event) =>
              dispatch(setCeilingShowOnlyLockedRooms(event.currentTarget.checked))
            }
          />
        </Group>
      </Alert>
    );
  }

  const topSimilarityPercent = getSimilarityPercent(sortedMatchingRooms[0]?.similarityScore ?? 0);
  const topSimilarityColor = getPercentColor(topSimilarityPercent);
  const titleText = `${matchingRooms.length} lignende rom har Multiplacer-komponenter`;
  const titleTooltip = `${matchingRooms.length} lignende ${roomName}-rom har lagrede Multiplacer-komponenter`;
  const bestMatchesText = sortedMatchingRooms
    .map((match) => `${match.roomNumber} (${formatSimilarity(match.similarityScore)})`)
    .join(", ");
  const compactBestMatchesText = compactBestMatches(sortedMatchingRooms);

  return (
    <Alert
      variant="light"
      icon={<FaInfoCircle size={14} />}
      py="xs"
      styles={{
        root: {
          backgroundColor: `${topSimilarityColor}1A`,
          borderColor: topSimilarityColor,
          paddingBlock: 8,
          paddingInline: 12,
        },
        icon: {
          color: topSimilarityColor,
        },
        body: {
          minWidth: 0,
        },
      }}
    >
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto auto",
          gap: 8,
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Box style={{ minWidth: 0 }}>
          <Tooltip label={titleTooltip} multiline maw={360} openDelay={300}>
            <Text size="xs" fw={600} truncate="end">
              {titleText}
            </Text>
          </Tooltip>
          <Tooltip label={`Beste treff: ${bestMatchesText}`} multiline maw={360} openDelay={300}>
            <Text size="xs" c="dimmed" truncate="end" style={{ minWidth: 0 }}>
              Beste treff: {compactBestMatchesText}
            </Text>
          </Tooltip>
        </Box>
        <Checkbox
          size="xs"
          label="Kun låste"
          checked={showOnlyLockedRooms}
          onChange={(event) => dispatch(setCeilingShowOnlyLockedRooms(event.currentTarget.checked))}
          styles={{ root: { flexShrink: 0 }, label: { whiteSpace: "nowrap" } }}
        />
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          <Badge
            size="sm"
            variant="filled"
            styles={{
              root: {
                backgroundColor: topSimilarityColor,
                color: "#ffffff",
                minWidth: 58,
              },
            }}
          >
            {topSimilarityPercent} %
          </Badge>
          <Button
            size="xs"
            variant="filled"
            color="dark"
            px={8}
            leftSection={<FaExternalLinkAlt size={11} />}
            onClick={() => setModalOpen(true)}
          >
            Vis rom
          </Button>
        </Group>
      </Box>
      {modalOpen && <SimilarRoomsWithDevicesModal onClose={() => setModalOpen(false)} />}
    </Alert>
  );
};
